/**
 * Инициализация Sentry для фронта
 *
 * Вызывается один раз в main.tsx ДО всех остальных импортов.
 *
 * Настройки:
 * - init только в production — dev локально не отправляет события (экономит квоту + не мешает отладке)
 * - sendDefaultPii: false — не собираем IP, user-agent по умолчанию (GDPR)
 * - tracesSampleRate: 0.1 — 10% трейсов для performance monitoring (MVP экономия квоты)
 * - replaysSessionSampleRate: 0 — replay выключен (видеозапись сессий = PII)
 * - replaysOnErrorSampleRate: 0.1 — только если произошла ошибка, 10% сессий
 *
 * Пользователь (id) выставляется отдельно через setSentryUser() из auth-хука.
 */

import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Не инициализируем если:
  // - нет DSN (локальная разработка без .env)
  // - dev режим (не хотим спамить в Sentry во время разработки)
  if (!dsn || !import.meta.env.PROD) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'production' / 'preview' / 'staging'

    // Integrations:
    // - browserTracingIntegration — performance monitoring (route changes, fetch/xhr)
    // - replayIntegration — Session Replay (видеозапись с маскировкой), включается только на error
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Маскируем весь текст (GDPR)
        blockAllMedia: true, // Блокируем картинки/видео (GDPR + экономия трафика)
      }),
    ],

    // Sampling
    tracesSampleRate: 0.1, // 10% трейсов для performance
    replaysSessionSampleRate: 0, // Без записи нормальных сессий
    replaysOnErrorSampleRate: 0.1, // 10% сессий с ошибкой

    // PII
    sendDefaultPii: false, // Не слать IP/user-agent по умолчанию

    // Release (подхватывается из env, выставляется на билде Vercel-ом или CI)
    release: import.meta.env.VITE_APP_VERSION,

    // Фильтрация "мусорных" ошибок которые не интересны
    ignoreErrors: [
      // Сетевые ошибки браузера — не баги, а условия окружения
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      // ResizeObserver — известный браузерный шум (безопасно игнорировать)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Отмена операций — не ошибка
      'AbortError',
      'The operation was aborted',
      // Расширения браузера (extensions кидают свои ошибки в window)
      /^Script error\.?$/,
    ],

    // Фильтрация запросов: не слать breadcrumbs из dev-инструментов
    beforeBreadcrumb(breadcrumb) {
      // Игнорируем HMR и Vite dev запросы
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const url = breadcrumb.data?.url as string | undefined;
        if (url?.includes('/@vite/') || url?.includes('/@fs/')) {
          return null;
        }
      }
      return breadcrumb;
    },
  });
}

/**
 * Выставить текущего пользователя в Sentry.
 * Вызывается после login из useCurrentUser.
 * GDPR: шлём только userId, без email/имени.
 */
export function setSentryUser(userId: string | null): void {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: userId });
}

/**
 * Ручная отправка ошибки в Sentry.
 * Используется из ErrorBoundary и handleApiError.
 */
export function captureException(
  error: unknown,
  context?: { tags?: Record<string, string>; extra?: Record<string, unknown> },
): void {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
  });
}
